import { Lock, Plus, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  CRITERION_TYPE_LABELS,
  effectiveFormFields,
  FIELD_VISIBILITIES,
  FIELD_VISIBILITY_LABELS,
  KNOCKOUT_ACTION_LABELS,
  KNOCKOUT_ACTIONS,
  KNOCKOUT_OPERATOR_LABELS,
  KNOCKOUT_OPERATORS_BY_TYPE,
  KNOCKOUT_STANDARD_FIELDS,
  knockoutRuleProblem,
  knockoutValueKind,
  QUESTION_TYPE_LABELS,
  QUESTION_TYPES,
  STANDARD_FIELD_BY_KEY,
  standardFieldOptions,
  type CriterionType,
  type FormFieldSettings,
  type KnockoutAction,
  type KnockoutRule,
  type QuestionType,
  type ScreeningQuestionInput,
  type StandardFieldKey,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Field, FieldRow } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { MultiSelect, Select } from '../../../components/Select';
import styles from './FormBuilder.module.css';

/**
 * Step 3 — the application form (F5.05) and its knockouts (F5.06, §L8).
 *
 * There is no CV parsing in V1 (D10), so this form is the ONLY structured data a
 * candidate record gets: make a thorough one easy. Standard fields are one click
 * each; a criterion from step 2 switches on the field it scores against (Q5), and
 * says so, so nobody wonders why a field will not hide.
 */

export type QuestionDraft = ScreeningQuestionInput & { readonly id: string };

interface FormBuilderProps {
  readonly formFields: FormFieldSettings;
  readonly questions: readonly QuestionDraft[];
  readonly criteriaTypes: readonly CriterionType[];
  readonly onChange: (next: { formFields: FormFieldSettings; questions: readonly QuestionDraft[] }) => void;
  readonly isReadOnly?: boolean;
}

export function FormBuilder({
  formFields,
  questions,
  criteriaTypes,
  onChange,
  isReadOnly = false,
}: FormBuilderProps): ReactNode {
  const fields = effectiveFormFields(formFields, criteriaTypes.map((type) => ({ type })));
  const custom = questions.filter((question) => question.standardField === null);
  const onFields = questions.filter((question) => question.standardField !== null);
  const setQuestions = (next: readonly QuestionDraft[]): void => onChange({ formFields, questions: next });
  const replace = (id: string, next: QuestionDraft): void =>
    setQuestions(questions.map((question) => (question.id === id ? next : question)));
  const remove = (id: string): void => setQuestions(questions.filter((question) => question.id !== id));
  const fieldsWithKnockout = new Set(onFields.map((question) => question.standardField));

  return (
    <div className={styles.builder}>
      <section className={styles.section} aria-labelledby="standard-fields">
        <h3 id="standard-fields" className={styles.heading}>
          Standard fields
        </h3>
        <ul className={styles.fields}>
          {fields.map((field) => (
            <li key={field.key} className={styles.field}>
              <span className={styles.fieldLabel}>{field.label}</span>
              {field.isLocked || field.requiredBy.length > 0 ? (
                <span className={styles.lockedNote}>
                  <Lock size={14} aria-hidden />
                  {field.isLocked ? 'Always required' : requiredByText(field.requiredBy)}
                </span>
              ) : (
                <Segmented
                  label={`${field.label}: required, optional or hidden`}
                  value={field.visibility}
                  disabled={isReadOnly}
                  options={FIELD_VISIBILITIES.map((option) => ({ value: option, label: FIELD_VISIBILITY_LABELS[option] }))}
                  onChange={(visibility) => onChange({ formFields: { ...formFields, [field.key]: visibility }, questions })}
                />
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="custom-questions">
        <h3 id="custom-questions" className={styles.heading}>
          Your questions
        </h3>
        {custom.length === 0 && (
          <p className={styles.empty}>No questions of your own yet.</p>
        )}
        <ol className={styles.questions}>
          {custom.map((question, index) => (
            <QuestionCard
              key={question.id}
              number={index + 1}
              question={question}
              isReadOnly={isReadOnly}
              onChange={(next) => replace(question.id, next)}
              onRemove={() => remove(question.id)}
            />
          ))}
        </ol>
        {!isReadOnly && (
          <Button variant="outline" icon={Plus} onClick={() => setQuestions([...questions, newQuestion()])}>
            Add a question
          </Button>
        )}
      </section>

      <section className={styles.section} aria-labelledby="field-knockouts">
        <h3 id="field-knockouts" className={styles.heading}>
          Knockouts on standard fields
        </h3>
        <p className={styles.hint}>
          For example, notice period of 30 days or fewer. The candidate never sees the rule.
        </p>
        <ol className={styles.questions}>
          {onFields.map((question) => (
            <QuestionCard
              key={question.id}
              question={question}
              isReadOnly={isReadOnly}
              onChange={(next) => replace(question.id, next)}
              onRemove={() => remove(question.id)}
            />
          ))}
        </ol>
        {!isReadOnly && (
          <div className={styles.addField}>
            <Select
              label="Add a knockout on a standard field"
              placeholder="Add a knockout on…"
              value={null}
              options={KNOCKOUT_STANDARD_FIELDS.filter((key) => !fieldsWithKnockout.has(key)).map((key) => ({
                value: key,
                label: STANDARD_FIELD_BY_KEY.get(key)?.label ?? key,
              }))}
              onChange={(key) => {
                const field = KNOCKOUT_STANDARD_FIELDS.find((candidate) => candidate === key);
                if (field) setQuestions([...questions, newFieldKnockout(field)]);
              }}
            />
          </div>
        )}
      </section>
    </div>
  );
}

function QuestionCard({
  number,
  question,
  isReadOnly,
  onChange,
  onRemove,
}: {
  readonly number?: number;
  readonly question: QuestionDraft;
  readonly isReadOnly: boolean;
  readonly onChange: (next: QuestionDraft) => void;
  readonly onRemove: () => void;
}): ReactNode {
  const isStandard = question.standardField !== null;
  const isChoice = question.type === 'single_select' || question.type === 'multi_select';
  const operators = KNOCKOUT_OPERATORS_BY_TYPE[question.type];
  const actions = operators.length === 0 ? (['none'] as const) : KNOCKOUT_ACTIONS;
  const problem =
    question.knockoutAction !== 'none' && question.knockoutRule
      ? knockoutRuleProblem(question.knockoutRule, question.type)
      : null;

  function setType(type: QuestionType): void {
    // A new type keeps the words but starts the knockout again: its operators differ.
    onChange({
      ...question,
      type,
      options: type === 'single_select' || type === 'multi_select' ? (question.options ?? []) : null,
      knockoutAction: 'none',
      knockoutRule: null,
      scorePenalty: null,
    });
  }

  function setAction(action: KnockoutAction): void {
    const firstOperator = operators[0];
    onChange({
      ...question,
      knockoutAction: action,
      knockoutRule: action === 'none' || !firstOperator ? null : (question.knockoutRule ?? { operator: firstOperator }),
      scorePenalty: action === 'score_penalty' ? (question.scorePenalty ?? 5) : null,
    });
  }

  return (
    <li className={styles.question}>
      <div className={styles.questionHead}>
        <span className={styles.questionNumber}>
          {question.standardField
            ? STANDARD_FIELD_BY_KEY.get(question.standardField)?.label
            : `Question ${number ?? ''}`}
        </span>
        {!isReadOnly && (
          <Button size="sm" variant="ghost" icon={Trash2} onClick={onRemove}>
            Remove
          </Button>
        )}
      </div>

      {!isStandard && (
        <>
          <FieldRow>
            <Field label="Question" isRequired>
              {({ inputId }) => (
                <Input
                  id={inputId}
                  disabled={isReadOnly}
                  value={question.questionText}
                  placeholder="Which accounting software have you used most?"
                  onChange={(event) => onChange({ ...question, questionText: event.target.value })}
                />
              )}
            </Field>
            <Field label="Answer type">
              {({ inputId }) => (
                <Select
                  id={inputId}
                  label="Answer type"
                  disabled={isReadOnly}
                  value={question.type}
                  options={QUESTION_TYPES.map((type) => ({ value: type, label: QUESTION_TYPE_LABELS[type] }))}
                  onChange={(next) => {
                    const type = QUESTION_TYPES.find((candidate) => candidate === next);
                    if (type) setType(type);
                  }}
                />
              )}
            </Field>
          </FieldRow>
          {isChoice && (
            <Field label="Options" isRequired hint="One per line. At least two.">
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  rows={3}
                  disabled={isReadOnly}
                  value={(question.options ?? []).join('\n')}
                  onChange={(event) => onChange({ ...question, options: event.target.value.split('\n') })}
                />
              )}
            </Field>
          )}
          <Checkbox
            checked={question.isRequired}
            disabled={isReadOnly}
            label="The candidate must answer"
            onChange={(checked) => onChange({ ...question, isRequired: checked })}
          />
        </>
      )}

      <div className={styles.knockout}>
        <Field label="Knockout">
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Knockout"
              disabled={isReadOnly || operators.length === 0}
              value={question.knockoutAction}
              options={actions.map((action) => ({ value: action, label: KNOCKOUT_ACTION_LABELS[action] }))}
              onChange={(next) => {
                const action = KNOCKOUT_ACTIONS.find((candidate) => candidate === next);
                if (action) setAction(action);
              }}
            />
          )}
        </Field>
        {question.knockoutAction !== 'none' && question.knockoutRule && (
          <RuleFields
            question={question}
            rule={question.knockoutRule}
            isReadOnly={isReadOnly}
            onChange={(rule) => onChange({ ...question, knockoutRule: rule })}
          />
        )}
        {question.knockoutAction === 'score_penalty' && (
          <Field label="Points off">
            {({ inputId }) => (
              <Input
                id={inputId}
                isNumeric
                inputMode="numeric"
                disabled={isReadOnly}
                value={String(question.scorePenalty ?? '')}
                onChange={(event) =>
                  onChange({ ...question, scorePenalty: Number(event.target.value.replace(/\D/g, '')) || null })
                }
              />
            )}
          </Field>
        )}
      </div>
      {question.knockoutAction !== 'none' && (
        <p className={styles.ruleNote} data-invalid={problem !== null || undefined}>
          {problem ?? 'Candidates who do not meet this condition are handled as chosen above.'}
        </p>
      )}
      {question.knockoutAction !== 'none' && !isReadOnly && (
        <FieldRow>
          <Field
            label="Group"
            isOptional
            hint="Questions in the same group can pass together — e.g. five years OR a certification."
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                value={question.groupKey ?? ''}
                placeholder="experience-or-cert"
                onChange={(event) => onChange({ ...question, groupKey: event.target.value.trim() || null })}
              />
            )}
          </Field>
          <Field label="Group rule">
            {() => (
              <Checkbox
                checked={question.isGroupOrLogic}
                disabled={question.groupKey === null}
                label="Pass if any question in the group passes"
                onChange={(checked) => onChange({ ...question, isGroupOrLogic: checked })}
              />
            )}
          </Field>
        </FieldRow>
      )}
    </li>
  );
}

/** The operator and the value input its kind needs (§L8). */
function RuleFields({
  question,
  rule,
  isReadOnly,
  onChange,
}: {
  readonly question: QuestionDraft;
  readonly rule: KnockoutRule;
  readonly isReadOnly: boolean;
  readonly onChange: (rule: KnockoutRule) => void;
}): ReactNode {
  const operators = KNOCKOUT_OPERATORS_BY_TYPE[question.type];
  const kind = knockoutValueKind(rule.operator, question.type);
  const options = choiceOptions(question);

  return (
    <>
      <Field label="Passes when the answer">
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Condition"
            disabled={isReadOnly}
            value={rule.operator}
            options={operators.map((operator) => ({ value: operator, label: KNOCKOUT_OPERATOR_LABELS[operator] }))}
            onChange={(next) => {
              const operator = operators.find((candidate) => candidate === next);
              if (operator) onChange({ operator });
            }}
          />
        )}
      </Field>
      {kind === 'number' && (
        <Field label="Value">
          {({ inputId }) => (
            <Input
              id={inputId}
              isNumeric
              inputMode="decimal"
              disabled={isReadOnly}
              value={typeof rule.value === 'number' ? String(rule.value) : ''}
              onChange={(event) => onChange({ ...rule, value: numberOrUndefined(event.target.value) })}
            />
          )}
        </Field>
      )}
      {kind === 'range' && (
        <Field label="From and to">
          {() => (
            <div className={styles.range}>
              <Input
                aria-label="From"
                isNumeric
                inputMode="decimal"
                disabled={isReadOnly}
                value={rule.min === undefined ? '' : String(rule.min)}
                onChange={(event) => onChange({ ...rule, min: numberOrUndefined(event.target.value) })}
              />
              <Input
                aria-label="To"
                isNumeric
                inputMode="decimal"
                disabled={isReadOnly}
                value={rule.max === undefined ? '' : String(rule.max)}
                onChange={(event) => onChange({ ...rule, max: numberOrUndefined(event.target.value) })}
              />
            </div>
          )}
        </Field>
      )}
      {kind === 'option' && options.length > 0 && (
        <Field label="Answer">
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Answer"
              disabled={isReadOnly}
              value={typeof rule.value === 'string' ? rule.value : null}
              options={options}
              onChange={(value) => onChange({ ...rule, value })}
            />
          )}
        </Field>
      )}
      {kind === 'options' && (
        <Field label="Answers">
          {({ inputId }) => (
            <MultiSelect
              id={inputId}
              label="Answers"
              disabled={isReadOnly}
              value={rule.values ?? []}
              options={options}
              onChange={(values) => onChange({ ...rule, values: [...values] })}
            />
          )}
        </Field>
      )}
      {(kind === 'text' || (kind === 'option' && options.length === 0)) && (
        <Field label="Text">
          {({ inputId }) => (
            <Input
              id={inputId}
              disabled={isReadOnly}
              value={typeof rule.value === 'string' ? rule.value : ''}
              onChange={(event) => onChange({ ...rule, value: event.target.value })}
            />
          )}
        </Field>
      )}
      {kind === 'date' && (
        <Field label="Date">
          {({ inputId }) => (
            <DatePicker
              id={inputId}
              label="Date"
              disabled={isReadOnly}
              value={typeof rule.value === 'string' ? rule.value : ''}
              onChange={(value) => onChange({ ...rule, value })}
            />
          )}
        </Field>
      )}
      {kind === 'days' && (
        <Field label="Days">
          {({ inputId }) => (
            <Input
              id={inputId}
              isNumeric
              inputMode="numeric"
              suffix="days"
              disabled={isReadOnly}
              value={rule.days === undefined ? '' : String(rule.days)}
              onChange={(event) => onChange({ ...rule, days: numberOrUndefined(event.target.value) })}
            />
          )}
        </Field>
      )}
    </>
  );
}

function choiceOptions(question: QuestionDraft): readonly { value: string; label: string }[] {
  if (question.standardField) return standardFieldOptions(question.standardField);
  return (question.options ?? [])
    .map((option) => option.trim())
    .filter((option) => option !== '')
    .map((option) => ({ value: option, label: option }));
}

function requiredByText(types: readonly CriterionType[]): string {
  const names = types.map((type) => CRITERION_TYPE_LABELS[type].toLowerCase()).join(' and ');
  return `Required — the ${names} criterion needs it`;
}

function numberOrUndefined(text: string): number | undefined {
  if (text.trim() === '') return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? value : undefined;
}

function newQuestion(): QuestionDraft {
  return {
    id: crypto.randomUUID(),
    standardField: null,
    questionText: '',
    type: 'text',
    options: null,
    isRequired: true,
    knockoutAction: 'none',
    knockoutRule: null,
    scorePenalty: null,
    groupKey: null,
    isGroupOrLogic: false,
  };
}

function newFieldKnockout(field: StandardFieldKey): QuestionDraft {
  const definition = STANDARD_FIELD_BY_KEY.get(field);
  const type = definition?.type ?? 'numeric';
  const operator = KNOCKOUT_OPERATORS_BY_TYPE[type][0];
  return {
    ...newQuestion(),
    standardField: field,
    questionText: definition?.label ?? field,
    type,
    knockoutAction: 'auto_flag',
    knockoutRule: operator ? { operator } : null,
  };
}
